import { useSyncExternalStore } from "react";
import { getApiUrl } from "@/store-redux";
import { KeyTokenName } from "@/constants";

export interface AiUpgradeTaskState {
  isGenerating: boolean;
  progress: number;
  targetBand: number;
  currentBand: number;
  prompt: string;
  originalEssay: string;
  upgradedResult: any;
  error: string | null;
  modalOpen: boolean;
  notifyCompleted: boolean;
  pageUrl: string;
  onApplyCallback?: (upgradedEssay: string) => void;
}

const WRITING_UPGRADE_CACHE_PREFIX = "ielts_writing_upgrade_cache_";

export const getUpgradeCacheKey = (prompt: string, answer: string, targetBand: number) => {
  const raw = `${prompt.trim()}||${answer.trim()}||${targetBand}`;
  let hash = 0;
  for (let i = 0; i < raw.length; i++) {
    const c = raw.charCodeAt(i);
    hash = ((hash << 5) - hash + c) | 0;
  }
  return `${WRITING_UPGRADE_CACHE_PREFIX}${Math.abs(hash)}`;
};

class AiUpgradeManager {
  private state: AiUpgradeTaskState = {
    isGenerating: false,
    progress: 0,
    targetBand: 0,
    currentBand: 0,
    prompt: "",
    originalEssay: "",
    upgradedResult: null,
    error: null,
    modalOpen: false,
    notifyCompleted: false,
    pageUrl: "",
  };

  private listeners: Set<() => void> = new Set();
  private progressTimer: any = null;

  public getState(): AiUpgradeTaskState {
    return this.state;
  }

  public subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private notify() {
    this.listeners.forEach((l) => l());
  }

  public setState(partial: Partial<AiUpgradeTaskState>) {
    this.state = { ...this.state, ...partial };
    this.notify();
  }

  public openModal() {
    this.setState({ modalOpen: true });
  }

  public closeModal() {
    this.setState({ modalOpen: false });
  }

  public reset() {
    if (this.progressTimer) {
      clearInterval(this.progressTimer);
      this.progressTimer = null;
    }
    this.setState({
      isGenerating: false,
      progress: 0,
      targetBand: 0,
      currentBand: 0,
      prompt: "",
      originalEssay: "",
      upgradedResult: null,
      error: null,
      modalOpen: false,
      notifyCompleted: false,
      pageUrl: "",
      onApplyCallback: undefined,
    });
  }

  public resetResult() {
    this.setState({ upgradedResult: null, error: null });
  }

  public startUpgrade = async (params: {
    prompt: string;
    originalEssay: string;
    currentBand: number;
    targetBand: number;
    pageUrl: string;
    onApplyCallback?: (upgradedEssay: string) => void;
  }) => {
    const { prompt, originalEssay, currentBand, targetBand, pageUrl, onApplyCallback } = params;

    const cacheKey = getUpgradeCacheKey(prompt, originalEssay, targetBand);
    try {
      const cachedRaw = localStorage.getItem(cacheKey);
      if (cachedRaw) {
        const cachedResult = JSON.parse(cachedRaw);
        if (
          cachedResult &&
          (cachedResult.upgradedEssay || (cachedResult.paragraphs && cachedResult.paragraphs.length > 0))
        ) {
          this.setState({
            prompt,
            originalEssay,
            currentBand,
            targetBand,
            pageUrl,
            onApplyCallback,
            upgradedResult: cachedResult,
            progress: 100,
            isGenerating: false,
            error: null,
            modalOpen: true,
          });
          return;
        }
      }
    } catch (e) {
      console.error("Lỗi đọc cache nâng band:", e);
    }

    if (this.progressTimer) {
      clearInterval(this.progressTimer);
    }

    this.setState({
      isGenerating: true,
      progress: 5,
      targetBand,
      currentBand,
      prompt,
      originalEssay,
      pageUrl,
      onApplyCallback,
      upgradedResult: null,
      error: null,
      modalOpen: true,
      notifyCompleted: false,
    });

    this.progressTimer = setInterval(() => {
      if (this.state.progress >= 95) return;
      const prev = this.state.progress;
      const step = prev < 30 ? 2 : prev < 75 ? 1 : 0.5;
      const nextProgress = Math.min(95, Math.round((prev + step) * 10) / 10);
      this.setState({ progress: nextProgress });
    }, 350);

    try {
      const token = localStorage.getItem(KeyTokenName);
      const url = getApiUrl("/writing-practices/suggest-upgrade-stream");

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          prompt,
          answer: originalEssay,
          currentBand,
          targetBand,
        }),
      });

      if (!response.ok || !response.body) {
        throw new Error(`HTTP Error status: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let buffer = "";

      const currentAccumulatedResult: any = {
        upgradedEssay: "",
        overallExplanation: "",
        paragraphs: [],
      };

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split("\n\n");
        buffer = events.pop() || "";

        for (const rawEvent of events) {
          if (!rawEvent.trim()) continue;

          let eventName = "message";
          let eventData = "";

          const lines = rawEvent.split("\n");
          for (const line of lines) {
            if (line.startsWith("event: ")) {
              eventName = line.substring(7).trim();
            } else if (line.startsWith("data: ")) {
              eventData = line.substring(6).trim();
            }
          }

          if (!eventData) continue;

          try {
            const data = JSON.parse(eventData);

            if (eventName === "init") {
              this.setState({ progress: Math.max(this.state.progress, 15) });
            } else if (eventName === "paragraph") {
              if (data.paragraph) {
                currentAccumulatedResult.paragraphs[data.index] = data.paragraph;
                const nextProgress = Math.min(92, Math.max(this.state.progress + 15, 30));
                this.setState({
                  upgradedResult: { ...currentAccumulatedResult },
                  progress: nextProgress,
                });
              }
            } else if (eventName === "complete") {
              if (this.progressTimer) {
                clearInterval(this.progressTimer);
                this.progressTimer = null;
              }

              const finalResultToSave =
                data && data.paragraphs && data.paragraphs.length > 0 ? data : currentAccumulatedResult;

              try {
                localStorage.setItem(cacheKey, JSON.stringify(finalResultToSave));
              } catch (cacheErr) {
                console.error("Lỗi lưu cache nâng band:", cacheErr);
              }

              this.setState({
                upgradedResult: finalResultToSave,
                progress: 100,
                isGenerating: false,
                notifyCompleted: true,
              });
            } else if (eventName === "error") {
              if (this.progressTimer) {
                clearInterval(this.progressTimer);
                this.progressTimer = null;
              }
              this.setState({
                isGenerating: false,
                error: data.message || "Xảy ra lỗi trong quá trình nâng band.",
              });
            }
          } catch (e) {
            console.error("Lỗi parse SSE event:", e, eventData);
          }
        }
      }
    } catch (err: any) {
      if (this.progressTimer) {
        clearInterval(this.progressTimer);
        this.progressTimer = null;
      }
      console.error("Lỗi gợi ý nâng band:", err);
      this.setState({
        isGenerating: false,
        error: err?.response?.data?.message || err.message || "Không thể kết nối đến server.",
      });
    }
  };
}

export const aiUpgradeManager = new AiUpgradeManager();

export const useAiUpgradeState = () => {
  return useSyncExternalStore(
    (listener) => aiUpgradeManager.subscribe(listener),
    () => aiUpgradeManager.getState()
  );
};
